/**
 * =============================================================================
 *  VELASCO LOCK AND SAFE — CENTRAL CONTENT CONFIG
 * =============================================================================
 *  This is the SINGLE place to edit business information.
 *  Update the phone number, email, reviews, services, projects, service areas,
 *  FAQ and social links here — the whole website updates automatically.
 *
 *  NOTE ON MEDIA: Real business photos supplied by the owner live in
 *  /public/media and are referenced via each media item's `src`. Sections
 *  without a supplied photo fall back to a labeled <MediaPlaceholder />.
 *  To add/replace media later, drop the file in /public/media and set `src`.
 * =============================================================================
 */

import {
  DoorOpen,
  Truck,
  PackageMinus,
  Wrench,
  KeyRound,
  ShieldCheck,
  Cpu,
  KeySquare,
  MapPin,
  Boxes,
  Gauge,
  Trash2,
  Replace,
  Lock,
  Unlock,
  ArrowLeftRight,
} from "lucide-react";

export const SITE_URL = "https://locksmithandsafes.com";

/* ---------------------------------------------------------------- BUSINESS */
export const business = {
  name: "Velasco Lock and Safe",
  owner: "Jose Velasco",
  phone: "(562) 716-4447",
  phoneTel: "+15627164447",
  email: "sprintstore0097@gmail.com",
  city: "Norwalk",
  state: "CA",
  region: "Southern California",
  serviceRadiusMiles: 40,
  tagline: "Safe & Vault Expertise You Can Trust",
  shortPitch:
    "Specialized safe and vault services — based in Norwalk and serving communities throughout Southern California.",
  credentials: "Licensed • Bonded • Insured",
  maxSafeWeight: "4,000 lbs",
  // Left intentionally blank until confirmed by owner. Shown as editable placeholders on the site.
  hours: "", // e.g. "Call for availability" — not published as a guarantee
  yearsInBusiness: "", // do not invent
  licenseNumber: "", // do not invent
};

/* ------------------------------------------------------------------ SOCIAL */
/* Replace "#" with real profile URLs when available. */
export const social = [
  { name: "Facebook", url: "#", handle: "Velasco Lock and Safe" },
  { name: "Instagram", url: "#", handle: "@velascolockandsafe" },
  { name: "TikTok", url: "#", handle: "@velascolockandsafe" },
  { name: "YouTube", url: "#", handle: "Velasco Lock and Safe" },
  { name: "Yelp", url: "#", handle: "Velasco Lock and Safe" },
];

/* -------------------------------------------------------------- NAVIGATION */
export const navLinks = [
  { label: "Home", path: "/" },
  { label: "Services", path: "/services" },
  { label: "Safe Services", path: "/safe-services" },
  { label: "Our Work", path: "/our-work" },
  { label: "Reviews", path: "/reviews" },
  { label: "About", path: "/about" },
  { label: "Service Areas", path: "/service-areas" },
  { label: "Careers", path: "/careers" },
  { label: "Contact", path: "/contact" },
];

/* ------------------------------------------------------------ TRUST POINTS */
/* Based ONLY on information supplied. No invented awards/years/licenses. */
export const trustPoints = [
  {
    icon: ShieldCheck,
    title: "Safe Specialists",
    body: "We take on difficult and specialized safe work — including jobs other companies turn down.",
  },
  {
    icon: Boxes,
    title: "All Types of Safes",
    body: "From everyday residential safes to oversized, high-security and heavy commercial units.",
  },
  {
    icon: Gauge,
    title: "Heavy Safe Moving",
    body: "Equipped for large, heavy safe moves up to 4,000 lbs with the proper equipment and a custom trailer.",
  },
  {
    icon: KeySquare,
    title: "Complete Safe & Vault Services",
    body: "Safe opening, moving, removal, disposal, installation, lock repair, keypad replacement and retrofits.",
  },
];

/* ------------------------------------------------------------- SAFE STATS  */
export const safeHighlights = [
  { value: "Up to 4,000", label: "lbs · heavy safe moving" },
  { value: "All Safes", label: "residential to high-security" },
  { value: "Vault", label: "& difficult safe work" },
  { value: "40 mi", label: "typical service radius" },
];

/* ---------------------------------------------------------------- SERVICES */
export const services = [
  {
    slug: "safe-opening",
    title: "Safe Opening",
    icon: DoorOpen,
    short: "Locked or malfunctioning safes evaluated and opened with expertise.",
    body: "Every safe is evaluated individually and opened using the right technique for that unit. Because each job is different, we assess your safe before determining the best approach.",
  },
  {
    slug: "safe-lockouts",
    title: "Safe Lockouts",
    icon: Unlock,
    short: "Locked out of your safe, or a failed lock or keypad?",
    body: "Locked out of your safe or dealing with a failed lock or keypad? Call to have the problem evaluated and determine the best approach for gaining access.",
  },
  {
    slug: "safe-moving",
    title: "Safe Moving",
    icon: Truck,
    short: "Professional safe relocation between homes and businesses.",
    body: "Careful, professional relocation of safes between residential and commercial locations using the proper equipment.",
  },
  {
    slug: "heavy-safe-moving",
    title: "Heavy Safe Moving",
    icon: Gauge,
    short: "Large, heavy safes — up to 4,000 lbs — moved safely.",
    body: "Specialized moving of large, heavy safes up to approximately 4,000 lbs — the oversized jobs that require experience, planning and a custom trailer.",
  },
  {
    slug: "safe-removal",
    title: "Safe Removal",
    icon: PackageMinus,
    short: "Removal and haul-away of unwanted or old safes.",
    body: "Removal of existing safes when you're clearing a property, downsizing or replacing a unit.",
  },
  {
    slug: "safe-disposal",
    title: "Safe Disposal",
    icon: Trash2,
    short: "Responsible disposal of safes you no longer need.",
    body: "Haul-away and disposal of old or unwanted safes so you don't have to deal with the weight and logistics yourself.",
  },
  {
    slug: "safe-installation",
    title: "Safe Installation",
    icon: ShieldCheck,
    short: "Placement, bolting and secure installation.",
    body: "Positioning, bolting and secure installation of safes so they're properly anchored and ready to use.",
  },
  {
    slug: "safe-lock-repair",
    title: "Safe Lock Repair",
    icon: Wrench,
    short: "Repair of mechanical and digital safe locks.",
    body: "Diagnosis and repair of mechanical dial and digital safe locks that are stuck, worn or malfunctioning.",
  },
  {
    slug: "keypad-replacement",
    title: "Safe Keypad Replacement",
    icon: Cpu,
    short: "Broken digital keypad replaced with a new unit.",
    body: "Replacement of broken or failing digital keypads and safe locks with new, working hardware.",
  },
  {
    slug: "lock-retrofitting",
    title: "Safe Lock Retrofitting",
    icon: Replace,
    short: "Upgrade or convert safe locking systems.",
    body: "Upgrade or retrofit safe locking systems, including conversions from mechanical combination locks to digital locking systems.",
  },
  {
    slug: "difficult-safe-access",
    title: "Difficult Safe Access",
    icon: KeyRound,
    short: "Complex, specialized and difficult safe jobs welcome.",
    body: "Difficult, oversized and specialized safe-opening and repair needs — including jobs other companies decline. Call so Jose can evaluate yours.",
  },
  {
    slug: "vault-work",
    title: "Vault Work",
    icon: Lock,
    short: "High-security safe and vault work.",
    body: "High-security safe and vault work evaluated on a case-by-case basis. Contact us with the details of your unit and project.",
  },
];

/* ----------------------------------------------------------- SAFE SERVICES */
export const safeServices = [
  {
    slug: "safe-opening",
    title: "Safe Opening",
    icon: DoorOpen,
    short: "Locked-out safes evaluated and opened with expertise.",
    body: "Jose evaluates each locked or malfunctioning safe individually and works to open it using the right technique for that unit.",
  },
  {
    slug: "safe-moving",
    title: "Safe Moving",
    icon: Truck,
    short: "Safe relocation between homes and businesses.",
    body: "Careful relocation of safes between residential and commercial locations using proper equipment.",
  },
  {
    slug: "heavy-safe-moving",
    title: "Heavy Safe Moving",
    icon: Gauge,
    short: "Large, heavy safes up to ~4,000 lbs moved safely.",
    body: "Specialized moving of large, heavy safes up to approximately 4,000 lbs — oversized jobs that require experience, planning and a custom trailer.",
  },
  {
    slug: "safe-removal",
    title: "Safe Removal",
    icon: PackageMinus,
    short: "Removal and haul-away of unwanted or old safes.",
    body: "Removal of existing safes when you're clearing a property, downsizing or replacing a unit.",
  },
  {
    slug: "safe-disposal",
    title: "Safe Disposal",
    icon: Trash2,
    short: "Responsible disposal of safes you no longer need.",
    body: "Haul-away and disposal of old or unwanted safes so you don't have to handle the weight and logistics.",
  },
  {
    slug: "safe-installation",
    title: "Safe Installation",
    icon: ShieldCheck,
    short: "Placement, bolting and secure installation.",
    body: "Positioning, bolting and secure installation of safes so they're anchored and ready to use.",
  },
  {
    slug: "safe-lock-repair",
    title: "Safe Lock Repair",
    icon: Wrench,
    short: "Repair of mechanical and digital safe locks.",
    body: "Diagnosis and repair of mechanical dial and digital safe locks that are stuck, worn or malfunctioning.",
  },
  {
    slug: "keypad-replacement",
    title: "Keypad Replacement",
    icon: Cpu,
    short: "Broken digital keypad replaced with a new unit.",
    body: "Replacement of broken or failing digital keypads and safe locks with new, working hardware.",
  },
  {
    slug: "mechanical-digital-locks",
    title: "Mechanical & Digital Locks",
    icon: KeyRound,
    short: "Service for both mechanical and digital safe locks.",
    body: "Work on both mechanical combination locks and digital/electronic safe locks — service, repair and replacement.",
  },
  {
    slug: "lock-conversion",
    title: "Mechanical-to-Digital Conversion",
    icon: ArrowLeftRight,
    short: "Convert combination dials to digital locks.",
    body: "Retrofit and convert mechanical combination locks to modern digital locking systems for easier, faster access.",
  },
  {
    slug: "difficult-safe-access",
    title: "Difficult Safe Access",
    icon: Unlock,
    short: "Complex, specialized and difficult safe jobs welcome.",
    body: "Difficult, oversized and specialized safe-opening and repair needs that some companies won't take on — call so Jose can evaluate yours.",
  },
  {
    slug: "vault-work",
    title: "High-Security & Vault Work",
    icon: Lock,
    short: "High-security safes and vault work evaluated case by case.",
    body: "High-security safe and vault work assessed on a case-by-case basis. Share the make, model and details of your unit when you call.",
  },
];

/* ------------------------------------------------------------ SAFE PROMISE */
export const safeStatement =
  "From residential and commercial safes to oversized, high-security and difficult safe projects, Velasco Lock and Safe evaluates a wide range of safe and vault work. Every job is different — call Jose so he can assess your specific safe before any work begins.";

/* ---------------------------------------------------------------- PROJECTS */
/* Our Work / Recent Projects. Real business photos supplied by the owner live in
   /public/media and are referenced via each media item's `src`.
   - To ADD a photo to a project: add { type: "image", src: "/media/your-file.jpg",
     alt: "...", label: "..." } to that project's `media` array.
   - To ADD a video later: add { type: "video", src: "/media/clip.mp4",
     poster: "/media/clip.jpg", label: "..." }.
   - A media item WITHOUT a `src` renders a labeled placeholder frame.
   - Use fit: "contain" for tall/portrait photos so nothing important is cropped.
   Order below matches the owner's supplied photo order. */
export const projects = [
  {
    id: "heavy-safe-relocation",
    title: "Heavy Safe Relocation",
    location: "Southern California",
    serviceType: "Heavy Safe Moving",
    description:
      "A large, heavy safe secured to our custom trailer and prepared for professional relocation using the proper safe-moving equipment.",
    media: [
      {
        type: "image",
        src: "/media/heavy-safe-relocation.jpg",
        alt: "Jose Velasco with a large safe strapped to the moving trailer",
        label: "Heavy Safe Relocation",
      },
    ],
    beforeAfter: false,
  },
  {
    id: "safe-moving-relocation",
    title: "Safe Moving / Relocation Project",
    location: "Southern California",
    serviceType: "Safe Moving",
    description:
      "Relocating a safe on a busy city street using specialized safe-moving equipment — the kind of careful, real-world work these jobs require.",
    media: [
      {
        type: "image",
        src: "/media/safe-moving-downtown.jpg",
        alt: "Velasco Lock and Safe crew relocating a safe on a city street",
        label: "Safe Relocation",
      },
    ],
    beforeAfter: false,
  },
  {
    id: "electronic-safe-lock",
    title: "Electronic Safe Lock Service",
    location: "Southern California",
    serviceType: "Safe Lock Service",
    description:
      "An electronic safe lock displaying a hardware-failure message — the type of malfunctioning electronic and digital safe lock we diagnose and service.",
    media: [
      {
        type: "image",
        src: "/media/electronic-safe-lock.jpg",
        alt: "Electronic safe lock display showing a hardware failure message",
        label: "Electronic Safe Lock",
        fit: "contain",
      },
    ],
    beforeAfter: false,
  },
  {
    id: "high-security-opening",
    title: "High-Security Safe Opening",
    location: "Southern California",
    serviceType: "Safe Opening",
    description:
      "High-security and difficult safe openings evaluated and handled individually.",
    media: [
      {
        type: "image",
        src: "/media/high-security-opening.webp",
        alt: "Jose working on a large high-security ISM Super Treasury safe",
        label: "High-Security Safe Opening",
        fit: "contain",
      },
    ],
    beforeAfter: false,
  },
];

/* ----------------------------------------------------------------- REVIEWS */
/* REAL customer reviews supplied by the owner. Do NOT invent reviews or present
   summaries as direct quotes.
   - type: "quote"   -> the customer's ACTUAL words (shown inside quotation marks).
   - type: "summary" -> a paraphrased summary of the review (shown WITHOUT quotes
     and labeled "Review summary"). Replace `text` with the exact Yelp wording and
     switch type to "quote" once the verbatim text is confirmed.
   Add new real reviews by copying an object below. */
export const reviews = [
  {
    name: "Derek L.",
    location: "Rancho Santa Margarita",
    service: "Safe Opening",
    type: "quote",
    text: "Excellent! Jose and his assistant came to open 3 safes. We were convinced they needed to be drilled but they were able to get all 3 open in less than an hour! Very friendly, experienced and professional, and very passionate about what they do.",
  },
  {
    name: "Lori S.",
    location: "Beverly Hills",
    service: "Safe Opening",
    type: "summary",
    text: "Contacted numerous locksmiths who all wanted to drill her high-quality safe. Jose evaluated the problem instead, opened the safe quickly, changed the code, and explained what had gone wrong — she praised his knowledge and service.",
  },
  {
    name: "Bruce D.",
    location: "Long Beach",
    service: "Heavy Safe Moving",
    type: "summary",
    text: "Jose moved an approximately 3,000-pound safe from North Hollywood to Montebello. Bruce describes him as responsive, reliable and easy to work with.",
  },
  {
    name: "Douglas C.",
    location: "Los Angeles",
    service: "Keypad Replacement",
    type: "quote",
    text: "Jose is a life saver! Our digital keypad broke on our safe and I called Jose, who came out next day and switched it out with a new one. Super friendly, super knowledgeable…",
  },
  {
    name: "Christian L.",
    location: "El Monte",
    service: "Heavy Safe Moving",
    type: "summary",
    text: 'Professional handling of a roughly 1,000-pound safe using the proper equipment and a custom trailer. Christian called it "a true 5 star service."',
  },
  {
    name: "Kyle G.",
    location: "Norwalk",
    service: "Safe Lock Repair",
    type: "summary",
    text: "Jose diagnosed a mechanical problem with the safe and opened it quickly. The job turned out easier than expected, so he lowered the price. Kyle highly recommends Velasco Lock and Safe.",
  },
];

/* ------------------------------------------------------------ SERVICE AREA */
export const serviceAreaIntro =
  "Based in Norwalk and serving customers throughout surrounding Southern California communities. Call to confirm service availability for your location and project.";

export const serviceAreas = [
  "Norwalk",
  "Downey",
  "La Mirada",
  "Montebello",
  "El Monte",
  "South Pasadena",
  "Long Beach",
  "Carson",
  "Los Angeles",
  "Santa Monica",
  "Beverly Hills",
  "Buena Park",
  "Fullerton",
  "Garden Grove",
  "Costa Mesa",
  "Newport Beach",
  "Rancho Santa Margarita",
];

/* --------------------------------------------------------------------- FAQ */
export const faqs = [
  {
    q: "What types of safes do you work on?",
    a: "We work on all types of safes — residential, commercial, gun safes, high-security units, oversized safes and vaults. Every safe is different, so the best first step is to call and describe yours so Jose can advise you.",
  },
  {
    q: "Can you open a safe without destroying it?",
    a: "In many cases, yes — Jose specializes in evaluating a safe and opening it using the least invasive method possible. However, every situation is different, so we never guarantee a non-destructive result before he has assessed your specific safe.",
  },
  {
    q: "Can you move very heavy safes?",
    a: "Yes. We regularly handle large, heavy safes and have the proper equipment and a custom trailer for oversized moves. Call with the safe's approximate weight, dimensions and the locations involved so we can plan the move.",
  },
  {
    q: "Can you replace a broken digital keypad or safe lock?",
    a: "Yes. We diagnose and replace failing digital keypads and repair mechanical and electronic safe locks. Let us know the make and model of your safe if you have it.",
  },
  {
    q: "Do you move safes between homes or businesses?",
    a: "Yes, we relocate safes between residential and commercial locations. Share the pickup and delivery details when you call so we can confirm access and equipment needs.",
  },
  {
    q: "Can you bolt or install a safe?",
    a: "Yes. We position, bolt and securely install safes. Placement and floor type matter, so it helps to discuss the location over the phone first.",
  },
  {
    q: "What information should I have ready when I call?",
    a: "Helpful details include the safe's brand and model, its approximate size and weight, what's happening with it, and your location. Photos can be very helpful — you're welcome to send them.",
  },
  {
    q: "What areas do you service?",
    a: "We're based in Norwalk and typically serve a wide area of surrounding Southern California communities. Because safe jobs vary, please call to confirm availability for your specific location and project.",
  },
  {
    q: "How do I get a quote?",
    a: "Give us a call. Safe and vault jobs vary too much for automatic online estimates — Jose prefers to understand the details of your job before providing pricing.",
  },
  {
    q: "Do I need to schedule online?",
    a: "No. We don't use online booking. The fastest way to get help is to call directly so we can talk through your job and arrange the right service.",
  },
];

/* --------------------------------------------------------------------- ABOUT */
export const about = {
  headline: "A hands-on locksmith and safe specialist",
  lead:
    "Velasco Lock and Safe is led by Jose Velasco — a hands-on locksmith and safe specialist who takes pride in the difficult jobs.",
  paragraphs: [
    "Jose focuses on doing the work himself and talking directly with each customer. That direct, personal approach is why so many people call after being turned away elsewhere — especially for challenging safe openings and heavy safe moves.",
    "The business specializes in a wide range of safe work: opening, moving, removal, installation and lock repair, alongside a full range of everyday locksmith services. From high-quality residential safes to oversized commercial units, Jose evaluates each job on its own terms.",
    "Backed by strong word-of-mouth from customers across Southern California, Velasco Lock and Safe has built a reputation for professionalism, honest advice and getting difficult jobs done right.",
  ],
  photoLabel: "Photo of Jose / Team",
};

/* ------------------------------------------------------------------ CAREERS */
export const careers = {
  headline: "Interested in Safe & Vault Work?",
  body:
    "Velasco Lock and Safe is interested in hearing from motivated people who want to learn more about professional safe and vault work. If you're curious about the trade and eager to learn, we'd like to hear from you.",
  note:
    "This is an open invitation to connect — not a specific job posting. Reach out by phone or message and tell us a little about yourself.",
};

export const iconMap = { MapPin };
